"use client";

import { useState, useRef, useEffect, type ReactNode } from "react";
import { Modal, Spin } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import {
  Category,
  getAllCategories,
} from "../../utils/apiSample/categories-data";
import {
  Cache_Category_Key,
  getCacheCategories,
  getPreferredCategories,
} from "../../utils/apiSample/recommendation";
import { appZIndex } from "../../utils/appconst";
import { PreferencesButton } from "../sharedcomponents/preferenceManager";
import { userGetBookCategories } from "../../apiservice/userService";
import { IBookCategoryData } from "../../apiservice/userService.type";
import { BookSliderSkeleton } from "../BookComponents/BookSkeletonLoading";

export const HomeCategoryCard = () => {
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [categories, setCategories] = useState<IBookCategoryData[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if we need to show navigation arrows
  useEffect(() => {
    const checkScroll = () => {
      if (!sliderRef.current) return;

      setShowLeftArrow(sliderRef.current.scrollLeft > 0);
      setShowRightArrow(
        sliderRef.current.scrollLeft <
          sliderRef.current.scrollWidth - sliderRef.current.clientWidth - 10
      );
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener("scroll", checkScroll);
      // Initial check
      checkScroll();
    }

    return () => {
      if (slider) {
        slider.removeEventListener("scroll", checkScroll);
      }
    };
  }, [categories]);

  // Scroll functions
  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  // Show Alert
  const showAlert = (title: string, content: ReactNode) => {
    Modal.info({
      title,
      content,
      onOk() {},
      zIndex: appZIndex.modal,
    });
  };

  // Show Confirm with navigation on new tab
  const showConfirm = (title: string, content: ReactNode, url: string) => {
    Modal.confirm({
      title,
      content,
      onOk() {
        window.open(url, "_blank");
      },
      onCancel() {
        console.log("Cancelled");
      },
      zIndex: appZIndex.modal,
    });
  };

  const loadCategories = async () => {
    let allCategories: IBookCategoryData[] = [];
    try {
      const cacheData = await getCacheCategories();
      allCategories = cacheData;
      setCategories(cacheData);
      const catgeoryData = await userGetBookCategories();
      if (catgeoryData) {
        setCategories(catgeoryData?.data);
        allCategories = catgeoryData?.data;
        localStorage.setItem(
          Cache_Category_Key,
          JSON.stringify(catgeoryData?.data)
        );
      } else {
        setError("Category not found");
      }
    } catch (err) {
      setError("Failed to load Category data");
    } finally {
      setLoading(false);
    }

    const preferredCategoryIds = getPreferredCategories();

    // If there are preferred categories, prioritize them
    if (preferredCategoryIds.length > 0) {
      // First add preferred categories
      const preferred = allCategories.filter((cat) =>
        preferredCategoryIds.includes(cat.id + "")
      );

      // Then add remaining categories
      const remaining = allCategories.filter(
        (cat) => !preferredCategoryIds.includes(cat.id + "")
      );

      setCategories([...preferred, ...remaining]);
    } else {
      setCategories(allCategories);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <BookSliderSkeleton title="Categories" height="12" />
      </div>
    );
  }

  if (error || !categories) {
    return (
      <div className="p-4 text-center text-red-500">
        {error || "Categories not found"}
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg tracking-tight text-gray-900 dark:text-stone-100">
          Categories
        </h3>
        <div className="flex items-center gap-2">
          <PreferencesButton
            onPreferencesUpdated={loadCategories}
            activeIndex="categories"
            buttonText="Edit Categories"
            modalTitle="Select Your Categories"
          />

          {showLeftArrow && (
            <button
              onClick={scrollLeft}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll left"
            >
              <LeftOutlined />
            </button>
          )}
          {showRightArrow && (
            <button
              onClick={scrollRight}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll right"
            >
              <RightOutlined />
            </button>
          )}
        </div>
      </div>

      <div
        ref={sliderRef}
        className="flex overflow-x-auto pb-2 gap-2 scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {categories.map((category, index: number) => {
          const isPreferred = getPreferredCategories().includes(
            category.id + ""
          );

          return (
            <Link
              key={index}
              to={`/users/book-list/${category.id}/${category.title}`}
              className="flex-shrink-0"
            >
              <div
                className={`px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap transition-colors ${
                  isPreferred
                    ? "bg-blue-800 border-blue-800 text-white dark:bg-blue-600 dark:border-blue-600 hover:opacity-90"
                    : "bg-white border-gray-200 text-gray-800 dark:bg-stone-800 dark:border-stone-700 dark:text-stone-200 hover:bg-gray-50 dark:hover:bg-stone-700"
                }`}
              >
                {category.title}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HomeCategoryCard;
